import React, { useState, useEffect, useMemo, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import ProductCard from './ProductCard';
import PageLayout from './PageLayout';
import FAQBox from './FAQBox';
import { useSearchParams } from 'react-router-dom';
import { MagnifyingGlassIcon, AdjustmentsHorizontalIcon, TagIcon, CurrencyDollarIcon, UserGroupIcon, SparklesIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ProductCardSkeleton from './ProductCardSkeleton';
import SEOHead from './SEOHead';
import { apiGet } from '../utils/api';
import { useDarkMode } from '../contexts/DarkModeContext';
import { trackProductLoad } from '../utils/performance';

const PRODUCTS_PER_PAGE = 20;

// Hero Banner Component
const HeroBanner = ({ isDarkMode }) => (
  <div className={`py-2 sm:py-6 lg:py-8 transition-colors duration-300 ${
    isDarkMode 
      ? 'bg-[#0f0f0f]' 
      : 'bg-gradient-to-br from-blue-50 to-white'
  }`}>
    <div className="max-w-[1525px] mx-auto px-4 sm:px-5">
      <div className="w-full flex justify-center relative" style={{zIndex: 1}}>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{zIndex: 0}}>
          <img 
            src="/images/logo.png" 
            alt="YTproud background" 
            width="320" height="320"
            style={{
              width: '200px',
              height: '200px',
              filter: 'blur(40px)',
              opacity: isDarkMode ? 0.15 : 0.12,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            className="sm:w-[320px] sm:h-[320px] sm:blur-[50px]"
            loading="eager"
            decoding="async"
          />
        </div>
        <div className={`rounded-2xl sm:rounded-3xl p-4 sm:p-8 lg:p-10 flex flex-col items-center justify-center text-white w-full mx-4 max-w-[1495px] mx-auto relative transition-all duration-500 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a] shadow-2xl shadow-black/50 border border-red-500/20' 
            : 'bg-gradient-to-br from-red-900 via-red-800 to-red-700 shadow-xl'
        }`} style={{zIndex: 1}}>
          {/* Animated background elements for dark mode */}
          {isDarkMode && (
            <div className="absolute inset-0 overflow-hidden rounded-2xl sm:rounded-3xl">
              <div className="absolute -top-6 -left-6 sm:-top-10 sm:-left-10 w-12 h-12 sm:w-20 sm:h-20 bg-red-400/10 rounded-full blur-xl animate-pulse"></div>
              <div className="absolute -bottom-6 -right-6 sm:-bottom-10 sm:-right-10 w-20 h-20 sm:w-32 sm:h-32 bg-blue-300/10 rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
              <div className="absolute top-1/2 left-1/4 w-10 h-10 sm:w-16 sm:h-16 bg-purple-500/10 rounded-full blur-lg animate-pulse" style={{animationDelay: '2s'}}></div>
              <div className="absolute top-1/4 right-1/4 w-8 h-8 sm:w-12 sm:h-12 bg-cyan-400/10 rounded-full blur-lg animate-pulse" style={{animationDelay: '0.5s'}}></div>
              <div className="absolute bottom-1/4 left-1/3 w-10 h-10 sm:w-14 sm:h-14 bg-pink-500/10 rounded-full blur-lg animate-pulse" style={{animationDelay: '1.5s'}}></div>
            </div>
          )}
          
          <div className="relative z-10">
            <h1 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-2 sm:mb-4 text-center transition-colors duration-300 ${
              isDarkMode 
                ? 'text-white drop-shadow-lg' 
                : 'text-white'
            }`}>
              Trusted Social Media Accounts Marketplace
            </h1>
            <p className={`text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-8 text-center max-w-2xl mx-auto transition-colors duration-300 ${
              isDarkMode 
                ? 'text-red-100' 
                : 'text-red-50'
            }`}>
              Buy or sell YouTube, Facebook, TikTok, and Instagram accounts.<br className="hidden sm:block" />
              Grow your online influence with <span className={`font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-yellow-300' : 'text-yellow-400'
              }`}>YTproud</span>.
            </p>
            <div className="flex items-center justify-center mb-4 sm:mb-8">
              <div className="flex items-center space-x-2 sm:space-x-4">
                {/* YouTube Icon */}
                <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-red-600 to-red-700 shadow-lg shadow-red-500/25' 
                    : 'bg-red-600 shadow-lg'
                }`}>
                  <img src="/images/categories/youtube.svg" alt="YouTube" className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                </div>
                
                {/* Facebook Icon */}
                <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25' 
                    : 'bg-blue-600 shadow-lg'
                }`}>
                  <img src="/images/categories/facebook.svg" alt="Facebook" className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                </div>
                
                {/* TikTok Icon */}
                <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-cyan-600 to-blue-600 shadow-lg shadow-cyan-500/25' 
                    : 'bg-gradient-to-br from-cyan-500 to-blue-500 shadow-lg'
                }`}>
                  <img src="/images/categories/tiktok.svg" alt="TikTok" className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                </div>
                
                {/* Instagram Icon */}
                <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-pink-600 to-purple-600 shadow-lg shadow-pink-500/25' 
                    : 'bg-gradient-to-br from-pink-500 to-purple-500 shadow-lg'
                }`}>
                  <img src="/images/categories/instagram.svg" alt="Instagram" className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                </div>
              </div>
            </div>
            <div className="flex justify-center w-full">
              <button
                className={`px-6 sm:px-8 py-2 sm:py-3 rounded-full font-bold transition-all duration-300 text-base sm:text-lg ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg shadow-red-500/25 hover:shadow-xl hover:shadow-red-500/40 transform hover:scale-105' 
                    : 'bg-white text-red-600 hover:bg-gray-100 shadow-lg'
                }`}
                onClick={() => {
                  const filterSection = document.querySelector('.filter-section');
                  if (filterSection) {
                    filterSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Explore Accounts
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Marketplace = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams] = useSearchParams();
  const [seoSettings, setSeoSettings] = useState(null);
  const { isDarkMode } = useDarkMode();
  const [filters, setFilters] = useState({
    platform: 'all',
    category: 'all',
    minPrice: '',
    maxPrice: '',
    minFollowers: '',
    maxFollowers: '',
    monetization: 'all',
    minIncome: '',
    maxIncome: '',
    sortBy: 'latest',
    search: searchParams.get('search') || ''
  });
  
  // Local search state for debouncing
  const [searchLocal, setSearchLocal] = useState(filters.search);
  const searchDebounceRef = useRef(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);

  // Minimal production-only client error reporter
  const safeReportError = async (message, context = {}) => {
    try {
      if (process.env.NODE_ENV !== 'production') return;
      const payload = { message: String(message || 'unknown_error'), context: { location: typeof window !== 'undefined' ? (window.location?.pathname || '/') : '/', ...context }, ts: Date.now() };
      await fetch('/api/client-logs', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true });
    } catch (_) {}
  };

  // Update filters when URL search params change
  useEffect(() => {
    const searchQuery = searchParams.get('search');
    if (searchQuery) {
      setFilters(prev => ({ ...prev, search: searchQuery }));
    }
  }, [searchParams]);

  // Fetch SEO settings for marketplace page
  useEffect(() => {
    const loadSeoSettings = async () => {
      try {
        const res = await apiGet('/api/page-seo/page/marketplace');
        const data = await res.json();
        setSeoSettings(data);
      } catch (error) {
        // Handle error silently for production - SEO settings will use defaults
        setSeoSettings(null);
        safeReportError('marketplace_seo_fetch_failed');
      }
    };
    loadSeoSettings();
  }, []);

  // Fetch products from the /boosted-random endpoint with caching
  useEffect(() => {
    const fetchProducts = async () => {
      const startTime = performance.now();
      try {
        setLoading(true);
        
        // Check if we have cached data
        const cachedData = sessionStorage.getItem('marketplace_products');
        const cacheTime = sessionStorage.getItem('marketplace_products_time');
        
        // Use cache if it's less than 5 minutes old AND contains data
        if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < 300000) {
          const data = JSON.parse(cachedData);

          
          // Only use cache if it actually has products
          if (Array.isArray(data) && data.length > 0) {
            setProducts(data); // Don't filter by status since products don't have status field
            setLoading(false);
            return;
          } else {

            // Clear the empty cache
            sessionStorage.removeItem('marketplace_products');
            sessionStorage.removeItem('marketplace_products_time');
          }
        }
        

        
        const response = await apiGet('/api/products/boosted-random');

        
        if (!response.ok) {
          const errorText = await response.text();

          throw new Error('Failed to fetch products');
        }
        
        const data = await response.json();

        
        if (Array.isArray(data) && data.length > 0) {
          const statuses = [...new Set(data.map(p => p.status))];

          
          // Check for boosted products
          const boostedProducts = data.filter(p => p.boosted);

        }
        
        // Show all products (since they don't have status field)
        const publishedProducts = Array.isArray(data) ? data : [];
        setProducts(publishedProducts);
        setError('');
        
        // Cache the data
        sessionStorage.setItem('marketplace_products', JSON.stringify(publishedProducts));
        sessionStorage.setItem('marketplace_products_time', Date.now().toString());
        
        // Track performance
        trackProductLoad(startTime, publishedProducts.length);
      } catch (error) {

        setError('Failed to load products. Please try again later.');
        setProducts([]);
        safeReportError('marketplace_products_fetch_failed');
      } finally {
        setLoading(false);

      }
    };

    fetchProducts();
  }, []);

  // Debounced search handler
  const onSearchChange = (e) => {
    const v = e.target.value;
    setSearchLocal(v);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: v }));
      setCurrentPage(1);
    }, 250);
  };

  // Memoized filtering for performance
  const filteredProducts = useMemo(() => {
    
    // Check for boosted products in the array
    const boostedProducts = products.filter(p => p.boosted);
    
    return products.filter(product => {
    // Platform filter: check both platform and accountType
    if (
      filters.platform !== 'all' &&
      product.platform !== filters.platform &&
      product.accountType !== filters.platform
    ) {

      return false;
    }
    // Category filter: check both category and stats?.category
    const prodCategory = product.stats?.category || product.category;
    if (filters.category !== 'all' && prodCategory !== filters.category) {

      return false;
    }
    if (filters.minPrice && product.price < Number(filters.minPrice)) return false;
    if (filters.maxPrice && product.price > Number(filters.maxPrice)) return false;
    if (filters.minFollowers && product.stats?.followers < Number(filters.minFollowers)) return false;
    if (filters.maxFollowers && product.stats?.followers > Number(filters.maxFollowers)) return false;
    if (filters.minIncome && product.stats?.monthlyIncome < Number(filters.minIncome)) return false;
    if (filters.maxIncome && product.stats?.monthlyIncome > Number(filters.maxIncome)) return false;
    if (filters.monetization !== 'all') {
      const monetizedStatus = product.stats?.monetized || product.stats?.monetization || product.monetized || product.monetization;
      const isMonetized = String(monetizedStatus).toLowerCase() === 'yes' || String(monetizedStatus).toLowerCase() === 'monetized' || monetizedStatus === true;
      if (filters.monetization === 'monetized' && !isMonetized) return false;
      if (filters.monetization === 'non-monetized' && isMonetized) return false;
    }
    if (filters.search && !product.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });
  }, [products, filters]);

  // Memoized sorting for performance
  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      if (filters.sortBy === 'price-low-high') return a.price - b.price;
      if (filters.sortBy === 'price-high-low') return b.price - a.price;
      // Default: latest (assuming _id or createdAt desc)
      return (b.createdAt || b._id) > (a.createdAt || a._id) ? 1 : -1;
    });
  }, [filteredProducts, filters.sortBy]);




  
  const totalPages = Math.ceil(sortedProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = sortedProducts.slice(
    (currentPage - 1) * PRODUCTS_PER_PAGE,
    currentPage * PRODUCTS_PER_PAGE
  );
  


  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Add a reset filters handler
  const handleResetFilters = () => {
    // Clear cache when resetting filters
    sessionStorage.removeItem('marketplace_products');
    sessionStorage.removeItem('marketplace_products_time');

    setFilters({
      platform: 'all',
      category: 'all',
      minPrice: '',
      maxPrice: '',
      minFollowers: '',
      maxFollowers: '',
      monetization: 'all',
      minIncome: '',
      maxIncome: '',
      sortBy: 'latest',
      search: ''
    });
    setCurrentPage(1);
  };

  // Pagination controls
  const renderPagination = () => (
    <div className="flex items-center gap-4 mt-10">
      {[...Array(totalPages)].map((_, idx) => (
        <button
          key={idx}
          onClick={() => setCurrentPage(idx + 1)}
          className={`w-14 h-12 rounded-full border text-lg font-medium transition-colors duration-150 focus:outline-none ${
            currentPage === idx + 1
              ? 'bg-red-600 text-white border-red-600'
              : isDarkMode
                ? 'bg-[#1a1a1a] text-gray-200 border-gray-600 hover:bg-[#2a2a2a]'
              : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'
          }`}
        >
          {idx + 1}
        </button>
      ))}
      <button
        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
        disabled={currentPage === totalPages}
        className={`w-20 h-12 rounded-full border text-lg font-medium transition-colors duration-150 focus:outline-none ${
          currentPage === totalPages
            ? isDarkMode
              ? 'bg-[#1a1a1a] text-gray-500 border-gray-700 cursor-not-allowed'
              : 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
        }`}
      >
        Next
      </button>
    </div>
  );

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Handle mobile footer filter button (from MobileFooterBar)
  useEffect(() => {
    const openHandler = () => setMobileFilterOpen(true);
    window.addEventListener('openMobileFilters', openHandler);
    return () => window.removeEventListener('openMobileFilters', openHandler);
  }, []);

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
    }`}>
      <SEOHead
        title={seoSettings?.title || 'Marketplace - YTProud'}
        description={seoSettings?.description || 'Browse and buy verified social media accounts from our secure marketplace.'}
        keywords={seoSettings?.keywords || 'social media marketplace, buy accounts, verified accounts'}
        ogTitle={seoSettings?.ogTitle || 'Marketplace - YTProud'}
        ogDescription={seoSettings?.ogDescription || 'Browse and buy verified social media accounts'}
        ogImage={seoSettings?.ogImage || '/images/marketplace-og.jpg'}
        schemaEnabled={seoSettings?.schemaEnabled || false}
        schemaJsonLd={seoSettings?.customSchemaJsonLd}
        twitterTitle={seoSettings?.twitterTitle || 'Marketplace - YTProud'}
        twitterDescription={seoSettings?.twitterDescription || 'Browse and buy verified social media accounts'}
        twitterImage={seoSettings?.twitterImage || '/images/marketplace-og.jpg'}
        canonical={seoSettings?.canonical || 'https://ytproud.com/marketplace'}
        noindex={seoSettings?.noindex || false}
        nofollow={seoSettings?.nofollow || false}
      />
      <Header />
      <HeroBanner isDarkMode={isDarkMode} />
      <PageLayout>
        {/* Modern Card-Style Filter Chips Bar - Desktop Only */}
        <div className="hidden md:block filter-section">
          <div className={`rounded-2xl shadow-lg p-6 lg:p-8 w-full mx-4 max-w-[1495px] mx-auto mb-4 sm:mb-8 transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-[#282828] shadow-2xl shadow-black/30' 
              : 'bg-white'
          }`}>
            <div className="flex flex-col gap-y-4 lg:gap-y-6 w-full">
              {/* Row 1 - Name/Type, Followers, Income, Price */}
              <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 items-center gap-3 lg:gap-4 xl:gap-5 w-full">
                {/* Name/Type Search (compact, labeled) */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Name/Type</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none focus-within:ring-2 focus-within:ring-blue-300 transition outline-none w-full text-sm lg:text-base ${
                    isDarkMode 
                      ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a]' 
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 focus-within:bg-gray-100'
                  }`}>
                    <MagnifyingGlassIcon className="w-4 h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6 text-gray-400" />
                                          <input
                        type="text"
                        name="search"
                        value={searchLocal}
                        onChange={onSearchChange}
                        placeholder="Search..."
                        className={`bg-transparent w-full px-1 py-1 outline-none border-none text-sm lg:text-base ${
                          isDarkMode ? 'text-gray-200 placeholder-gray-400' : 'text-gray-700 placeholder-gray-500'
                        }`}
                      />
                  </div>
                </div>
                {/* Followers (labeled) */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Followers</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                    isDarkMode 
                      ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' 
                      : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span className="text-sm lg:text-base">👥</span>
                    <input
                      type="number"
                      name="minFollowers"
                      value={filters.minFollowers}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode 
                          ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                          : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`mx-1 lg:mx-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>-</span>
                    <input
                      type="number"
                      name="maxFollowers"
                      value={filters.maxFollowers}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode 
                          ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                          : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
                {/* Income (labeled) */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Income</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                    isDarkMode 
                      ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' 
                      : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span className="text-sm lg:text-base">💰</span>
                    <input
                      type="number"
                      name="minIncome"
                      value={filters.minIncome}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode 
                          ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                          : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`mx-1 lg:mx-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>-</span>
                    <input
                      type="number"
                      name="maxIncome"
                      value={filters.maxIncome}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode 
                          ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                          : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
                {/* Price (labeled) */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Price</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                    isDarkMode 
                      ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' 
                      : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span className={`text-sm lg:text-base ${isDarkMode ? 'text-emerald-400' : 'text-green-600'}`}>$</span>
                    <input
                      type="number"
                      name="minPrice"
                      value={filters.minPrice}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode 
                          ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                          : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`mx-1 lg:mx-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>-</span>
                    <input
                      type="number"
                      name="maxPrice"
                      value={filters.maxPrice}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode 
                          ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                          : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
              </div>
              {/* Row 2 - All Platforms, All Categories, All Monetization, Latest, Reset Filters */}
              <div className="grid grid-cols-2 lg:grid-cols-5 xl:grid-cols-5 items-center gap-3 lg:gap-4 xl:gap-6 w-full">
                {/* Platform Chip */}
                <div className="col-span-1">
              <select
                name="platform"
                value={filters.platform}
                onChange={handleFilterChange}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' 
                        : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                    }`}
              >
                <option value="all">All Platforms</option>
                <option value="youtube">YouTube</option>
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="facebook">Facebook</option>
              </select>
            </div>
                {/* Category Chip */}
                <div className="col-span-1">
              <select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' 
                        : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                    }`}
              >
                <option value="all">All Categories</option>
                <option value="Gaming">Gaming</option>
                <option value="Business">Business</option>
                <option value="Music">Music</option>
                <option value="Fashion">Fashion</option>
                <option value="Travel">Travel</option>
                <option value="Fitness">Fitness</option>
                <option value="Pets">Pets</option>
                <option value="Education">Education</option>
                <option value="Tech">Tech</option>
                <option value="Vlogs">Vlogs</option>
                <option value="Entertainment">Entertainment</option>
                <option value="News">News</option>
              </select>
            </div>
                {/* Monetization Chip */}
                <div className="col-span-1">
              <select
                name="monetization"
                value={filters.monetization}
                onChange={handleFilterChange}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' 
                        : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                    }`}
              >
                <option value="all">All Monetization</option>
                <option value="monetized">Monetized</option>
                <option value="non-monetized">Non-Monetized</option>
              </select>
            </div>
                {/* Sort By Chip */}
                <div className="col-span-1">
                  <select
                    name="sortBy"
                    value={filters.sortBy}
                onChange={handleFilterChange}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-semibold shadow-sm border transition outline-none text-sm lg:text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 border-white/20 focus:ring-2 focus:ring-white/20 focus:border-white/40' 
                        : 'bg-white text-gray-900 border-black/10 focus:ring-2 focus:ring-black/10 focus:border-black/20'
                    }`}
                  >
                    <option value="latest">Latest</option>
                    <option value="price-low-high">Price: Low to High</option>
                    <option value="price-high-low">Price: High to Low</option>
                  </select>
            </div>
                                {/* Reset Filters Button */}
                <div className="col-span-1">
            <button
              onClick={handleResetFilters}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-bold shadow-sm transition border-none text-sm lg:text-base w-full max-w-[200px] mx-auto lg:mx-0 xl:mx-0 ${
                      isDarkMode 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : 'bg-blue-900 text-white hover:bg-blue-800'
                    }`}
            >
              Reset Filters
            </button>
          </div>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile Filter Button removed as per request */}
        {/* Product Grid */}
        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto">
              {Array.from({ length: 20 }).map((_, i) => <ProductCardSkeleton key={i} />)}
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-600 text-lg">{error}</div>
            </div>
          ) : paginatedProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1">
              {/* Shopping bags SVG icon from profile.js */}
              <svg width="80" height="80" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-4">
                <rect x="14" y="20" width="24" height="28" rx="3" fill="#F87171" stroke="#111" strokeWidth="2"/>
                <rect x="28" y="28" width="18" height="20" rx="2.5" fill="#FBBF24" stroke="#111" strokeWidth="2"/>
                <path d="M20 20V16a8 8 0 0116 0v4" stroke="#111" strokeWidth="2"/>
                <circle cx="22" cy="32" r="2" fill="#fff" stroke="#111" strokeWidth="1.5"/>
                <circle cx="34" cy="32" r="2" fill="#fff" stroke="#111" strokeWidth="1.5"/>
              </svg>
              <h2 className={`text-xl sm:text-2xl font-bold mb-2 text-center transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>
                No accounts found
              </h2>
              <p className={`text-center max-w-md transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-500'
              }`}>
                We couldn't find any social media accounts matching your filters. Try adjusting your search or check back soon for new listings.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto">
              {paginatedProducts.map((product, i) => (
                <ProductCard
                  key={product._id || i + (currentPage - 1) * PRODUCTS_PER_PAGE}
                  product={product}
                  width={360}
                />
              ))}
            </div>
          )}
        </div>
        {/* Pagination Controls */}
        {!loading && !error && paginatedProducts.length > 0 && (
          <div className="max-w-[1495px] mx-auto">{renderPagination()}</div>
        )}
        
        {/* Marketplace FAQs */}
        <FAQBox
          title="Frequently Asked Questions"
          faqs={[
            { 
              q: 'Is YTProud a trusted and safe marketplace to buy social media accounts?', 
              a: 'Absolutely. YTProud is a safe and verified marketplace for YouTube, TikTok, Instagram, and Facebook accounts. Every listing is manually reviewed, escrow protects payments, and our 24/7 support team ensures secure transfers.' 
            },
            { 
              q: 'What types of social media accounts are available on YTProud?', 
              a: 'YTProud offers verified accounts for creators, marketers, and businesses:\n\n• Monetized & non-monetized YouTube channels\n• TikTok accounts, including Creativity Beta & TikTok Shop profiles\n• High-engagement Instagram accounts\n• Facebook pages with active communities\n\n👉 <a href="/marketplace" class="underline text-blue-600 hover:text-blue-800">Browse our full marketplace listings to find the right account for you.</a>' 
            },
            { 
              q: 'How does the account transfer process work after purchasing?', 
              a: 'Once you purchase, YTProud\'s escrow team guides the secure transfer. Your email is added as the new manager/owner, and the process is verified to be fast, safe, and stress-free.' 
            },
            { 
              q: 'Can I contact the seller before buying a social media account?', 
              a: 'Yes. YTProud\'s live chat lets you ask questions and verify details directly with the seller. For security, we recommend keeping all communication inside the YTProud platform.' 
            },
            { 
              q: 'What if I face problems after buying a social media account on YTProud?', 
              a: 'YTProud provides full buyer protection. If issues arise, our team helps with ownership transfers, dispute resolution, and ensures you receive the account as described.' 
            },
          ]}
        />
        {/* Mobile Filter Drawer */}
        {mobileFilterOpen && (
          <div className="fixed inset-0 z-50 flex md:hidden">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black bg-opacity-40 transition-opacity"
              onClick={() => setMobileFilterOpen(false)}
            />
            {/* Drawer */}
            <div className={`ml-auto w-full max-w-sm h-full shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
          isDarkMode 
            ? 'bg-[#282828] shadow-black/50' 
            : 'bg-white'
        }`}>
              <div className={`flex items-center justify-between p-4 border-b transition-colors duration-300 ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <span className={`text-lg font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Filters</span>
                <button onClick={() => setMobileFilterOpen(false)} aria-label="Close filters">
                  <XMarkIcon className={`w-7 h-7 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                </button>
              </div>
              <div className="overflow-y-auto p-4 flex-1">
                {/* Filter form (reuse the same filter controls as in the main bar) */}
                {/* Row 1 */}
                <div className="flex flex-col gap-y-4 w-full mb-4">
                  <select
                    name="platform"
                    value={filters.platform}
                    onChange={handleFilterChange}
                    className={`px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' 
                        : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                    }`}
                  >
                    <option value="all">All Platforms</option>
                    <option value="youtube">YouTube</option>
                    <option value="instagram">Instagram</option>
                    <option value="tiktok">TikTok</option>
                    <option value="facebook">Facebook</option>
                  </select>
                  <select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    className={`px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' 
                        : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                    }`}
                  >
                    <option value="all">All Categories</option>
                    <option value="Gaming">Gaming</option>
                    <option value="Business">Business</option>
                    <option value="Music">Music</option>
                    <option value="Fashion">Fashion</option>
                    <option value="Travel">Travel</option>
                    <option value="Fitness">Fitness</option>
                    <option value="Pets">Pets</option>
                    <option value="Education">Education</option>
                    <option value="Tech">Tech</option>
                    <option value="Vlogs">Vlogs</option>
                    <option value="Entertainment">Entertainment</option>
                    <option value="News">News</option>
                  </select>
                  <select
                    name="monetization"
                    value={filters.monetization}
                    onChange={handleFilterChange}
                    className={`px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' 
                        : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                    }`}
                  >
                    <option value="all">All Monetization</option>
                    <option value="monetized">Monetized</option>
                    <option value="non-monetized">Non-Monetized</option>
                  </select>
                  <select
                    name="sortBy"
                    value={filters.sortBy}
                    onChange={handleFilterChange}
                    className={`px-4 py-3 rounded-full font-semibold shadow-sm border transition outline-none text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 border-white/20 focus:ring-2 focus:ring-white/20 focus:border-white/40' 
                        : 'bg-white text-gray-900 border-black/10 focus:ring-2 focus:ring-black/10 focus:border-black/20'
                    }`}
                  >
                    <option value="latest">Latest</option>
                    <option value="price-low-high">Price: Low to High</option>
                    <option value="price-high-low">Price: High to Low</option>
                  </select>
                </div>
                {/* Row 2 */}
                <div className="flex flex-col gap-y-4 w-full">
                  <div className="flex flex-col items-start w-full">
                    <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>Name/Type</span>
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none w-full text-base ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-blue-400' 
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-blue-300'
                    }`}>
                      <MagnifyingGlassIcon className={`w-5 h-5 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-400'
                      }`} />
                      <input
                        type="text"
                        name="search"
                        value={searchLocal}
                        onChange={onSearchChange}
                        placeholder="Search..."
                        className={`bg-transparent w-full px-1 py-1 outline-none border-none text-base transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-200 placeholder-gray-400' : 'text-gray-700 placeholder-gray-500'
                        }`}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-start w-full">
                    <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>Followers</span>
                    <div className={`flex items-center gap-6 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' 
                        : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                    }`}>
                      <span className="">👥</span>
                      <input
                        type="number"
                        name="minFollowers"
                        value={filters.minFollowers}
                        onChange={handleFilterChange}
                        min="0"
                        placeholder="Min"
                        className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm transition-colors duration-300 ${
                          isDarkMode 
                            ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                            : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                        }`}
                      />
                      <span className={`mx-2 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>-</span>
                      <input
                        type="number"
                        name="maxFollowers"
                        value={filters.maxFollowers}
                        onChange={handleFilterChange}
                        min="0"
                        placeholder="Max"
                        className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm transition-colors duration-300 ${
                          isDarkMode 
                            ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                            : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                        }`}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-start w-full">
                    <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>Income</span>
                    <div className={`flex items-center gap-6 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' 
                        : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                    }`}>
                      <span className="">💰</span>
                      <input
                        type="number"
                        name="minIncome"
                        value={filters.minIncome}
                        onChange={handleFilterChange}
                        min="0"
                        placeholder="Min"
                        className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm transition-colors duration-300 ${
                          isDarkMode 
                            ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                            : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                        }`}
                      />
                      <span className={`mx-2 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>-</span>
                      <input
                        type="number"
                        name="maxIncome"
                        value={filters.maxIncome}
                        onChange={handleFilterChange}
                        min="0"
                        placeholder="Max"
                        className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm transition-colors duration-300 ${
                          isDarkMode 
                            ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                            : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                        }`}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-start w-full">
                    <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>Price</span>
                    <div className={`flex items-center gap-6 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                      isDarkMode 
                        ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' 
                        : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                    }`}>
                      <span className={`${isDarkMode ? 'text-emerald-400' : 'text-green-600'}`}>$</span>
                      <input
                        type="number"
                        name="minPrice"
                        value={filters.minPrice}
                        onChange={handleFilterChange}
                        min="0"
                        placeholder="Min"
                        className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm transition-colors duration-300 ${
                          isDarkMode 
                            ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                            : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                        }`}
                      />
                      <span className={`mx-2 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>-</span>
                      <input
                        type="number"
                        name="maxPrice"
                        value={filters.maxPrice}
                        onChange={handleFilterChange}
                        min="0"
                        placeholder="Max"
                        className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm transition-colors duration-300 ${
                          isDarkMode 
                            ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' 
                            : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                        }`}
                      />
                    </div>
                  </div>
                </div>
                {/* Bottom row with Reset and Search buttons, only on mobile */}
                <div className="flex gap-4 mt-6 md:hidden">
                  <button
                    onClick={() => { handleResetFilters(); setMobileFilterOpen(false); }}
                    className={`flex-1 px-4 py-4 rounded-full font-bold shadow-sm transition border-none text-base ${
                      isDarkMode 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : 'bg-blue-900 text-white hover:bg-blue-800'
                    }`}
                  >
                    Reset Filters
                  </button>
                  <button
                    onClick={() => setMobileFilterOpen(false)}
                    className={`flex-1 px-4 py-4 rounded-full font-bold shadow-sm transition border-none text-base ${
                      isDarkMode 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </PageLayout>
      <Footer />
    </div>
  );
};

export default Marketplace; 